/**
 * Loại bỏ dấu tiếng Việt khỏi chuỗi
 * @param str Chuỗi cần xử lý
 * @returns Chuỗi không dấu
 * Sử dụng normalize("NFD") để tách dấu ra khỏi ký tự gốc.
Dùng regex /[\u0300-\u036f]/g để loại bỏ dấu thanh (́, ̀, ̃, ̣, ̉...).
 
 * Loại bỏ dấu tiếng Việt khỏi chuỗi và thay thế "đ" thành "d"
 
 */
export function removeVietnameseAccents(str: string): string {
    return str
        .normalize("NFD") // Tách dấu khỏi ký tự
        .replace(/[\u0300-\u036f]/g, "") // Loại bỏ dấu thanh
        .replace(/đ/g, "d") // Chuyển "đ" thành "d"
        .replace(/Đ/g, "D"); // Chuyển "Đ" thành "D"
}
 /**
 * Tìm vị trí chuỗi không dấu trong chuỗi có dấu
 * @param text Chuỗi gốc (có dấu)
 * @param search Chuỗi tìm kiếm (không dấu)
 * @returns Vị trí đầu tiên của chuỗi tìm thấy hoặc -1 nếu không tìm thấy
 * SearchText không dấu, text có dấu
 */
export function indexOfVietnamese(text: string, search: string): number {
    // Chuyển cả hai chuỗi về không dấu và chữ thường
    const textNoAccent = removeVietnameseAccents(text.toLowerCase());
    const searchNoAccent = removeVietnameseAccents(search.toLowerCase());

    return textNoAccent.indexOf(searchNoAccent); // Tìm vị trí xuất hiện đầu tiên
}
/**
 * Tìm kiếm và highlight chuỗi không dấu trong văn bản có dấu
 * @param text Chuỗi gốc (có dấu)
 * @param search Chuỗi tìm kiếm (không dấu)
 * @returns Chuỗi đã highlight nếu tìm thấy, ngược lại trả về chuỗi rỗng
 */
export function highlightVietnamese(text: string, search: string): string {
    if (!search.trim()) return ""; // Nếu không có từ khóa tìm kiếm, trả về rỗng

    const textNoAccent = removeVietnameseAccents(text.toLowerCase());
    const searchNoAccent = removeVietnameseAccents(search.toLowerCase());

    const index = textNoAccent.indexOf(searchNoAccent);
    if (index === -1) return ""; // Nếu không tìm thấy, trả về chuỗi rỗng

    // Tạo regex để tìm kiếm chính xác
    const regex = new RegExp(`(${searchNoAccent})`, "gi");

    // Thay thế từ khóa tìm thấy bằng <mark>
    return text.replace(regex, `<mark>$1</mark>`);
}
/**
 * Highlight từ khóa tiếng Việt không dấu trong văn bản có dấu
 * @param text Văn bản gốc (có dấu)
 * @param search Từ khóa tìm kiếm (không dấu)
 * @returns Văn bản với từ khóa được highlight, nếu không tìm thấy thì trả về văn bản gốc
 */
export function highlightVietnameseHTML(text: string, search: string): string {
    if (!search.trim()) return text; // Nếu từ khóa rỗng, trả về văn bản gốc

    // Loại bỏ dấu và chuyển về chữ thường
    const textNoAccent = removeVietnameseAccents(text.toLowerCase());
    const searchNoAccent = removeVietnameseAccents(search.toLowerCase());

    // Tìm vị trí từ khóa không dấu trong văn bản không dấu
    let index = textNoAccent.indexOf(searchNoAccent);
    if (index === -1) return ""; // Không tìm thấy, trả về rỗng

    // Tìm đoạn chính xác trong văn bản có dấu
    let result = "";
    let lastIndex = 0;

    while (index !== -1) {
        // Cắt phần trước từ khóa
        result += text.substring(lastIndex, index);
        // Cắt từ khóa đúng trong văn bản có dấu và bọc trong <mark>
        result += `<mark style="display: inline;">${text.substring(index, index + search.length)}</mark>`;

        // Cập nhật lại index mới
        lastIndex = index + search.length;
        index = textNoAccent.indexOf(searchNoAccent, lastIndex);
    }

    // Thêm phần còn lại của văn bản
    result += text.substring(lastIndex);
    return result;
}
/**
 * Loại bỏ tất cả thẻ <mark> khỏi chuỗi HTML
 * @param html Chuỗi HTML đầu vào
 * @returns Chuỗi HTML sau khi loại bỏ <mark>
 */
export function removeAllMarks(html: string): string {
    return html.replace(/<\/?mark[^>]*>/gi, ""); // Xóa cả thẻ mở và đóng <mark>
}
/**
 * Gộp các thẻ `<a style="color:red;">` liên tiếp lại thành một thẻ duy nhất
 * @param divElement Phần tử div cần chỉnh sửa
 */
export function mergeAdjacentRedLinks(divElement: HTMLElement) {
    let anchorNodes = Array.from(divElement.querySelectorAll("a[style='color:red;']")) as HTMLElement[];

    for (let i = 0; i < anchorNodes.length - 1; i++) {
        let currentA = anchorNodes[i];
        let nextA = anchorNodes[i + 1];

        // Nếu hai thẻ `<a>` đứng sát nhau, gộp lại
        if (currentA.nextSibling === nextA) {
            currentA.innerHTML += nextA.innerHTML;
            nextA.remove(); // Xóa thẻ `<a>` sau khi gộp
            i--; // Kiểm tra lại với phần tử mới gộp
        }
    }
}

// ✅ Kiểm tra hàm tìm kiếm
// const text = "Tìm kiếm văn bản có dấu nhưng hỗ trợ tìm không dấu.";
// console.log(indexOfVietnamese(text, "tim kiem")); // 🟢 Kết quả: 0
// console.log(indexOfVietnamese(text, "van ban")); // 🟢 Kết quả: 9
// console.log(indexOfVietnamese(text, "khong dau")); // 🟢 Kết quả: 38
// console.log(indexOfVietnamese(text, "không dấu")); // 🟢 Kết quả: 38
// console.log(indexOfVietnamese(text, "abcxyz")); // 🔴 Kết quả: -1 (Không tìm thấy)
